/**
 * The feedback form as data: what the two kinds are called, what the form is
 * allowed to send, and what the panel says back. The component below is wiring
 * over this — every rule about when Send is live, how much room is left and
 * what a player is told afterwards is asserted at tier 3.
 */
import {
  MAX_FEEDBACK_MESSAGE,
  MIN_FEEDBACK_MESSAGE,
  type FeedbackContext,
  type FeedbackKind,
} from '@tv-casino/shared';
import type { FeedbackReceipt } from '@tv-casino/shared';

export interface FeedbackKindCopy {
  /** The tab label. */
  label: string;
  /** The heading over the box once the kind is picked. */
  heading: string;
  placeholder: string;
}

export const FEEDBACK_COPY: Record<FeedbackKind, FeedbackKindCopy> = {
  bug: {
    label: 'Something broke',
    heading: 'What went wrong?',
    placeholder: 'My split hand paid twice, on the blackjack table, right after the dealer busted…',
  },
  feature: {
    label: 'Idea',
    heading: 'What should we add?',
    placeholder: 'Let the host mute the table from the TV…',
  },
};

/** Warn about the cap only when someone is actually near it. */
const COUNTDOWN_FROM = 200;

export type FeedbackStatus = 'idle' | 'sending' | 'sent' | 'error';

export interface FeedbackFormState {
  kind: FeedbackKind;
  message: string;
  contact: string;
  status: FeedbackStatus;
  receipt: FeedbackReceipt | null;
  error: string | null;
}

export const emptyFeedbackForm = (kind: FeedbackKind = 'bug'): FeedbackFormState => ({
  kind,
  message: '',
  contact: '',
  status: 'idle',
  receipt: null,
  error: null,
});

export interface FeedbackFormView {
  heading: string;
  placeholder: string;
  /** Characters still allowed; only worth showing once `showCount` is true. */
  remaining: number;
  showCount: boolean;
  canSend: boolean;
  sendLabel: string;
  /** Set once it landed — the thanks replaces the form. */
  thanks: string | null;
  /** The filed issue, when GitHub took it. */
  issueUrl: string | null;
  issueLabel: string | null;
  error: string | null;
}

export function feedbackFormView(state: FeedbackFormState): FeedbackFormView {
  const trimmed = state.message.trim();
  const remaining = MAX_FEEDBACK_MESSAGE - state.message.length;
  const sent = state.status === 'sent';
  const receipt = state.receipt;
  return {
    heading: FEEDBACK_COPY[state.kind].heading,
    placeholder: FEEDBACK_COPY[state.kind].placeholder,
    remaining,
    showCount: remaining <= COUNTDOWN_FROM,
    canSend: state.status !== 'sending' && trimmed.length >= MIN_FEEDBACK_MESSAGE,
    sendLabel: state.status === 'sending' ? 'Sending…' : 'Send it',
    thanks: sent
      ? receipt?.filed
        ? 'Thanks — that is on the list.'
        : 'Thanks — that reached us.'
      : null,
    issueUrl: sent && receipt?.filed ? receipt.issueUrl : null,
    issueLabel:
      sent && receipt?.filed && receipt.issueNumber !== null ? `Issue #${receipt.issueNumber}` : null,
    error: state.status === 'error' ? state.error : null,
  };
}

/** What the phone can say about where it was standing. */
export interface FeedbackWhere {
  path: string;
  build: string;
  casinoCode?: string | null;
  tableCode?: string | null;
  gameType?: string | null;
  width?: number;
  height?: number;
}

/**
 * The context stamped onto the report. Only what helps reproduce a bug: the
 * page, the build, the table, the screen. Empty fields are left out entirely
 * rather than sent as nulls, so the issue's table has no blank rows.
 */
export function feedbackContext(where: FeedbackWhere): FeedbackContext {
  const context: FeedbackContext = { path: where.path, build: where.build };
  if (where.casinoCode) context.casinoCode = where.casinoCode;
  if (where.tableCode) context.tableCode = where.tableCode;
  if (where.gameType) context.gameType = where.gameType;
  if (where.width && where.height) context.viewport = `${where.width}×${where.height}`;
  return context;
}
